/**
 * <lane-analytics-form> Web Component
 * 
 * A self-contained filter form that controls one or more <lane-analytics> elements.
 * Changes are applied immediately (no apply button needed).
 * 
 * Includes a multi-select dataset filter that shows all available datasets
 * with checkboxes and select/deselect all.
 * 
 * Attributes:
 *   - target:     CSS selector or comma-separated IDs of <lane-analytics> elements
 *   - language:   Language code (it, en, fr, de, es, ja, zh). Default: "it"
 *   - days:       Default lookback period in days. Default: 7
 *   - begin-date: Initial begin date (ISO string). Overrides `days` if set.
 *   - end-date:   Initial end date (ISO string). Default: now
 *   - show-separate-by: Show "Separate by" dropdown. Default: true
 *   - show-type:  Show counter/duration radio. Default: true
 *   - show-mode:  Show mode dropdown (trend/distribution). Default: true
 *   - show-span:  Show span dropdown. Default: false
 * 
 * Events:
 *   - analytics-form-change: Fired on every change. Detail contains all form values.
 */
class LaneAnalyticsForm extends HTMLElement {
    static get observedAttributes() {
        return ["language", "days", "begin-date", "end-date", "target"];
    }

    constructor() {
        super();
        this._shadowRoot = this.attachShadow({ mode: "open" });
        this._translations = null;
        this._initialized = false;
        this._debounceTimer = null;
        this._datasetLabels = []; // populated by panel after chart renders
    }

    connectedCallback() {
        this._render();
        this._initialized = true;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this._initialized && oldValue !== newValue) {
            if (name === "language") {
                this._translations = null;
                this._render();
            }
        }
    }

    disconnectedCallback() {
        const form = this._shadowRoot.querySelector("form");
        if (form) {
            form.removeEventListener("change", this._boundOnChange);
            form.removeEventListener("input", this._boundOnInput);
        }
        if (this._debounceTimer) clearTimeout(this._debounceTimer);
    }

    get formValues() {
        const form = this._shadowRoot.querySelector("form");
        if (!form) return {};

        // Get dates — handle both flatpickr (text) and native datetime-local
        let beginDate = "";
        let endDate = "";

        if (this._fpFrom && this._fpFrom.selectedDates.length) {
            beginDate = this._fpFrom.selectedDates[0].toISOString();
        } else {
            const beginInput = form.querySelector("#begin-date");
            beginDate = beginInput?.value || "";
        }

        if (this._fpTo && this._fpTo.selectedDates.length) {
            endDate = this._fpTo.selectedDates[0].toISOString();
        } else {
            const endInput = form.querySelector("#end-date");
            endDate = endInput?.value || "";
        }

        return {
            beginDate,
            endDate,
            separateBy: form.querySelector("#separate-by")?.value ?? this.getAttribute("datasets") ?? "",
            type: form.querySelector("#type")?.value || this.getAttribute("type") || "counter",
            mode: form.querySelector("#mode")?.value || this.getAttribute("mode") || "trend",
            span: form.querySelector("#span")?.value || this.getAttribute("span") || "",
        };
    }

    /**
     * Returns the list of hidden dataset indices (unchecked in the filter).
     */
    get hiddenDatasets() {
        const container = this._shadowRoot.querySelector("#dataset-filter-list");
        if (!container) return [];
        const hidden = [];
        const checkboxes = container.querySelectorAll("input[type='checkbox'][data-index]");
        checkboxes.forEach(cb => {
            if (!cb.checked) hidden.push(parseInt(cb.dataset.index, 10));
        });
        return hidden;
    }

    /**
     * Called externally (by panel) to populate the dataset filter with labels.
     * Preserves previous check/uncheck state by matching on label name.
     * @param {string[]} labels - Array of dataset label strings from the chart
     */
    updateDatasetFilter(labels) {
        // Build the set of hidden label names from the most authoritative source available:
        // 1. _hiddenByLabel is already up-to-date (set by checkbox change or legend click) → keep it
        // 2. Fallback: read the live checkbox DOM (handles edge cases where _hiddenByLabel is stale)
        // We only read from DOM when _hiddenByLabel doesn't exist yet (first render with no prior state).
        if (!this._hiddenByLabel) {
            // First time — derive from DOM if checkboxes already exist
            const previouslyHidden = new Set();
            if (this._datasetLabels.length > 0) {
                const container = this._shadowRoot.querySelector("#dataset-filter-list");
                if (container) {
                    container.querySelectorAll("input[type='checkbox'][data-index]").forEach(cb => {
                        if (!cb.checked) {
                            const idx = parseInt(cb.dataset.index, 10);
                            if (this._datasetLabels[idx]) {
                                previouslyHidden.add(this._datasetLabels[idx]);
                            }
                        }
                    });
                }
            }
            this._hiddenByLabel = previouslyHidden;
        }
        // If _hiddenByLabel already exists it is always current — no need to rebuild it.
        // _renderDatasetFilter will use it as-is and also re-fire _onDatasetFilterChange
        // so the chart visibility stays in sync with the (possibly new) dataset indices.

        this._datasetLabels = labels;
        this._renderDatasetFilter();
    }

    // --- Private methods ---

    async _loadTranslation() {
        if (this._translations) return this._translations;
        const lang = (this.getAttribute("language") || "it").toLowerCase();
        const suppLangs = ["it", "fr", "en", "de", "ja", "es", "zh"];
        const resolvedLang = suppLangs.includes(lang) ? lang : "it";

        try {
            const response = await fetch(`/analytics/1.0/res/translations/${resolvedLang}.json`);
            if (response.ok) {
                this._translations = await response.json();
                return this._translations;
            }
        } catch (e) {
            console.warn("[lane-analytics-form] Could not load translations", e);
        }

        this._translations = {};
        return this._translations;
    }

    _t(key) {
        return (this._translations && this._translations[key]) || key;
    }

    async _render() {
        await this._loadTranslation();
        const showSeparateBy = !this.hasAttribute("show-separate-by") || this.getAttribute("show-separate-by") !== "false";
        const showType = !this.hasAttribute("show-type") || this.getAttribute("show-type") !== "false";
        const showMode = !this.hasAttribute("show-mode") || this.getAttribute("show-mode") !== "false";
        const showSpan = this.getAttribute("show-span") === "true";

        // Calculate default dates
        const days = parseInt(this.getAttribute("days") || "7", 10);
        let beginDate = this.getAttribute("begin-date");
        let endDate = this.getAttribute("end-date");

        if (!beginDate) {
            const d = new Date();
            d.setDate(d.getDate() - days);
            d.setHours(0, 0, 0, 0);
            beginDate = this._toLocalDateTimeString(d);
        } else {
            beginDate = this._toLocalDateTimeString(new Date(beginDate));
        }

        if (!endDate) {
            const d = new Date();
            d.setHours(23, 59, 0, 0);
            endDate = this._toLocalDateTimeString(d);
        } else {
            endDate = this._toLocalDateTimeString(new Date(endDate));
        }

        this._shadowRoot.innerHTML = `
            <style>${this._getStyles()}</style>
            <form class="analytics-form">
                <div class="form-group">
                    <label for="begin-date">${this._t("From")}</label>
                    <input type="datetime-local" id="begin-date" class="form-control" value="${beginDate}">
                </div>
                <div class="form-group">
                    <label for="end-date">${this._t("To")}</label>
                    <input type="datetime-local" id="end-date" class="form-control" value="${endDate}">
                </div>
                ${showType ? this._renderTypeDropdown() : ""}
                ${showMode ? this._renderModeDropdown() : ""}
                ${showSeparateBy ? this._renderSeparateBy() : ""}
                <div class="form-group" id="dataset-filter-container"></div>
                ${showSpan ? this._renderSpanDropdown() : ""}
            </form>
        `;

        // Attach event listeners — every change triggers immediate apply
        this._boundOnChange = this._onAnyChange.bind(this);
        this._boundOnInput = this._onDateInput.bind(this);

        const form = this._shadowRoot.querySelector("form");
        form.addEventListener("change", this._boundOnChange);
        form.addEventListener("input", this._boundOnInput);

        // Populate dynamic dropdowns with initial options based on mode
        this._updateDynamicDropdowns();

        // Set initial value for separate-by after options are populated
        const initialDatasets = this.getAttribute("datasets") || "";
        if (initialDatasets) {
            const separateBySelect = form.querySelector("#separate-by");
            if (separateBySelect) separateBySelect.value = initialDatasets;
        }

        // Set initial value for span if attribute is provided
        const initialSpan = this.getAttribute("span") || "";
        if (initialSpan) {
            const spanSelect = form.querySelector("#span");
            if (spanSelect) spanSelect.value = initialSpan;
        }

        // Re-render dataset filter if we already have labels
        if (this._datasetLabels.length > 0) {
            this._renderDatasetFilter();
        }

        // Progressive enhancement: use flatpickr if available on the host page
        this._initFlatpickr();
    }

    /** Initialize flatpickr on date inputs if window.flatpickr is available */
    _initFlatpickr() {
        if (typeof window.flatpickr !== 'function') return;

        const lang = (this.getAttribute("language") || "it").toLowerCase();
        const locale = (window.flatpickr.l10ns && window.flatpickr.l10ns[lang]) ? lang : 'default';

        const fpOpts = {
            enableTime: true,
            dateFormat: 'd/m/Y H:i',
            time_24hr: true,
            allowInput: true,
            locale: locale,
            onChange: (selectedDates) => {
                if (selectedDates.length) {
                    this._applyNow();
                }
            }
        };

        const fromEl = this._shadowRoot.querySelector("#begin-date");
        const toEl = this._shadowRoot.querySelector("#end-date");

        if (fromEl) {
            // Change input type to text so flatpickr can control it
            fromEl.type = "text";
            const fpFrom = window.flatpickr(fromEl, { ...fpOpts });
            const beginVal = fromEl.getAttribute("value");
            if (beginVal) fpFrom.setDate(new Date(beginVal), false);
            this._fpFrom = fpFrom;
        }

        if (toEl) {
            toEl.type = "text";
            const fpTo = window.flatpickr(toEl, { ...fpOpts });
            const endVal = toEl.getAttribute("value");
            if (endVal) fpTo.setDate(new Date(endVal), false);
            this._fpTo = fpTo;
        }
    }

    /** Renders the multi-select dataset filter inside the form */
    _renderDatasetFilter() {
        const container = this._shadowRoot.querySelector("#dataset-filter-container");
        if (!container) return;

        // Hide if no datasets or only one dataset
        if (this._datasetLabels.length <= 1) {
            container.innerHTML = "";
            container.style.display = "none";
            return;
        }

        container.style.display = "flex";

        const selectAllLabel = this._t("Select all") || "Select all";
        const datasetsLabel = this._t("Datasets") || "Datasets";

        let html = `<label>${datasetsLabel}</label>`;
        html += `<div class="dataset-filter-box" id="dataset-filter-list">`;
        html += `<label class="dataset-checkbox select-all-label">`;
        html += `<input type="checkbox" id="dataset-select-all" checked>`;
        html += `<span class="select-all-text">${selectAllLabel}</span></label>`;
        html += `<div class="dataset-divider"></div>`;

        this._datasetLabels.forEach((label, index) => {
            const isChecked = !(this._hiddenByLabel && this._hiddenByLabel.has(label));
            html += `<label class="dataset-checkbox">`;
            html += `<input type="checkbox" data-index="${index}"${isChecked ? " checked" : ""}>`;
            html += `<span class="dataset-label-text" title="${label}">${label}</span></label>`;
        });

        html += `</div>`;
        container.innerHTML = html;

        // Wire up select-all
        const selectAll = container.querySelector("#dataset-select-all");
        // Set initial state of select-all based on current checkboxes
        const allCbs = container.querySelectorAll("input[data-index]");
        const allChecked = [...allCbs].every(c => c.checked);
        const someChecked = [...allCbs].some(c => c.checked);
        selectAll.checked = allChecked;
        selectAll.indeterminate = !allChecked && someChecked;

        selectAll.addEventListener("change", (e) => {
            const checked = e.target.checked;
            container.querySelectorAll("input[data-index]").forEach(cb => {
                cb.checked = checked;
            });
            this._onDatasetFilterChange();
        });

        // Wire up individual checkboxes
        container.querySelectorAll("input[data-index]").forEach(cb => {
            cb.addEventListener("change", () => {
                // Update select-all state
                const all = container.querySelectorAll("input[data-index]");
                const allChecked = [...all].every(c => c.checked);
                selectAll.checked = allChecked;
                selectAll.indeterminate = !allChecked && [...all].some(c => c.checked);
                this._onDatasetFilterChange();
            });
        });

        // Re-apply hidden state to the chart after re-render
        if (this._hiddenByLabel && this._hiddenByLabel.size > 0) {
            this._onDatasetFilterChange();
        }
    }

    /** Called when dataset filter checkboxes change — toggle chart visibility */
    _onDatasetFilterChange() {
        this.dispatchEvent(new CustomEvent("analytics-dataset-filter", {
            bubbles: true,
            composed: true,
            detail: { hiddenIndices: this.hiddenDatasets }
        }));
    }

    _renderSeparateBy() {
        return `
            <div class="form-group">
                <label for="separate-by">${this._t("Separate by")}</label>
                <select id="separate-by" class="form-control"></select>
            </div>
        `;
    }

    _renderTypeDropdown() {
        const options = [
            { value: "counter", label: this._t("Transits count") },
            { value: "duration", label: this._t("Transits duration") },
        ];
        const optionsHtml = options.map(o => `<option value="${o.value}">${o.label}</option>`).join("");
        return `
            <div class="form-group">
                <label for="type">${this._t("Measure")}</label>
                <select id="type" class="form-control">${optionsHtml}</select>
            </div>
        `;
    }

    _renderModeDropdown() {
        const currentValue = this.getAttribute("mode") || "trend";
        const options = [
            { value: "trend", label: this._t("trend") },
            { value: "distribution", label: this._t("distribution") },
        ];
        const optionsHtml = options.map(o =>
            `<option value="${o.value}"${o.value === currentValue ? " selected" : ""}>${o.label}</option>`
        ).join("");
        return `
            <div class="form-group">
                <label for="mode">${this._t("Mode")}</label>
                <select id="mode" class="form-control">${optionsHtml}</select>
            </div>
        `;
    }

    _renderSpanDropdown() {
        return `
            <div class="form-group">
                <label for="span">${this._t("Show data by")}</label>
                <select id="span" class="form-control"></select>
            </div>
        `;
    }

    /**
     * Returns the available options for "Separa per" based on current mode and span.
     */
    _getSeparateByOptions(mode, span) {
        const allOptions = [
            { value: "", label: this._t("No selection") },
            { value: "lane", label: this._t("lane") },
            { value: "place", label: this._t("place") },
            { value: "direction", label: this._t("direction") },
            { value: "vehicle_class", label: this._t("vehicle_class") },
            { value: "month", label: this._t("month") },
            { value: "year", label: this._t("year") },
            { value: "day", label: this._t("day") },
            { value: "tenant", label: this._t("Tenant") },
        ];

        if (mode === "trend") {
            // Trend: no date/time-based datasets
            const excluded = ["month", "year", "day"];
            return allOptions.filter(o => !excluded.includes(o.value));
        }

        if (mode === "distribution" && span === "daymonth") {
            // Distribution with span=daymonth: no year, month, weekday
            const excluded = ["year", "month", "day"];
            return allOptions.filter(o => !excluded.includes(o.value));
        }

        return allOptions;
    }

    /**
     * Returns the available options for "Mostra dati per" based on current mode.
     */
    _getSpanOptions(mode) {
        const temporalOptions = [
            { value: "quarterly", label: this._t("quarterly") },
            { value: "hour", label: this._t("hour") },
            { value: "day", label: this._t("day") },
            { value: "daymonth", label: this._t("daymonth") },
            { value: "week", label: this._t("week") },
            { value: "month", label: this._t("month") },
            { value: "year", label: this._t("year") },
        ];

        const nonTemporalOptions = [
            { value: "place", label: this._t("place") },
            { value: "lane", label: this._t("lane") },
            { value: "direction", label: this._t("direction") },
            { value: "vehicle_class", label: this._t("vehicle_class") },
            { value: "tenant", label: this._t("Tenant") },
        ];

        if (mode === "trend") {
            // Trend: only temporal spans (no daymonth), no non-temporal
            return temporalOptions.filter(o => o.value !== "daymonth");
        }

        // Distribution: temporal + non-temporal
        return [...temporalOptions, ...nonTemporalOptions];
    }

    /**
     * Updates the options in "Separa per" and "Mostra dati per" dropdowns
     * based on current mode and span values. Preserves current selection if still valid.
     */
    _updateDynamicDropdowns() {
        const form = this._shadowRoot.querySelector("form");
        if (!form) return;

        const modeSelect = form.querySelector("#mode");
        const spanSelect = form.querySelector("#span");
        const separateBySelect = form.querySelector("#separate-by");

        const mode = modeSelect?.value || "trend";
        const span = spanSelect?.value || "";

        if (spanSelect) {
            const currentSpan = spanSelect.value;
            const spanOptions = this._getSpanOptions(mode);
            spanSelect.innerHTML = spanOptions.map(o =>
                `<option value="${o.value}"${o.value === currentSpan ? " selected" : ""}>${o.label}</option>`
            ).join("");
            if (!spanOptions.some(o => o.value === currentSpan)) {
                spanSelect.value = spanOptions[0]?.value || "";
            }
        }

        if (separateBySelect) {
            const currentSep = separateBySelect.value;
            const updatedSpan = spanSelect?.value || span;
            const sepOptions = this._getSeparateByOptions(mode, updatedSpan);
            separateBySelect.innerHTML = sepOptions.map(o =>
                `<option value="${o.value}"${o.value === currentSep ? " selected" : ""}>${o.label}</option>`
            ).join("");
            if (!sepOptions.some(o => o.value === currentSep)) {
                separateBySelect.value = "";
            }
        }
    }

    _toLocalDateTimeString(date) {
        const pad = (n) => String(n).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    /** Called on select/radio change — apply immediately */
    _onAnyChange(e) {
        // Don't trigger full re-render for dataset filter checkbox changes
        if (e.target.closest("#dataset-filter-container")) return;

        // If mode or span changed, update the dynamic dropdowns
        if (e.target.id === "mode" || e.target.id === "span") {
            this._updateDynamicDropdowns();
        }

        this._applyNow();
    }

    /** Called on date input typing — debounce 400ms then apply (native inputs only) */
    _onDateInput(e) {
        if (e.target.type === "datetime-local") {
            if (this._debounceTimer) clearTimeout(this._debounceTimer);
            this._debounceTimer = setTimeout(() => this._applyNow(), 400);
        }
    }

    /** Apply form values to targets and dispatch event */
    _applyNow() {
        const values = this.formValues;
        this.dispatchEvent(new CustomEvent("analytics-form-change", {
            bubbles: true,
            composed: true,
            detail: values
        }));
        this._applyToTargets(values);
    }

    /**
     * Applies form values to target <lane-analytics> elements (standalone mode).
     */
    _applyToTargets(values) {
        const targetAttr = this.getAttribute("target");
        if (!targetAttr) return;

        let elements = [];
        try {
            elements = [...document.querySelectorAll(targetAttr)];
        } catch (e) {
            const ids = targetAttr.split(",").map(id => id.trim());
            elements = ids
                .map(id => document.getElementById(id) || document.querySelector(`[id="${id}"]`))
                .filter(Boolean);
        }

        for (const el of elements) {
            if (el.tagName.toLowerCase() !== "lane-analytics") continue;
            this._applyValuesToElement(el, values);
        }
    }

    /**
     * Applies form values to a single <lane-analytics> element and triggers re-render.
     */
    _applyValuesToElement(el, values) {
        if (values.beginDate) {
            const d = new Date(values.beginDate);
            if (!isNaN(d.getTime())) {
                el.setAttribute("begin-date", d.toISOString());
            }
        }
        if (values.endDate) {
            const d = new Date(values.endDate);
            if (!isNaN(d.getTime())) {
                el.setAttribute("end-date", d.toISOString());
            }
        }
        if (values.separateBy) {
            el.setAttribute("datasets", values.separateBy);
        } else {
            el.removeAttribute("datasets");
        }
        if (values.type) {
            el.setAttribute("type", values.type);
        }
        if (values.mode) {
            el.setAttribute("mode", values.mode);
        } else {
            el.removeAttribute("mode");
        }
        if (values.span) {
            el.setAttribute("span", values.span);
        }
        // Toggle render to trigger refresh
        const currentRender = el.getAttribute("render");
        el.setAttribute("render", currentRender === "false" ? "true" : "false");
    }

    _getStyles() {
        return `
            :host {
                display: block;
                font-family: 'Arial', sans-serif;
                font-size: 14px;
                color: #495057;
            }
            .analytics-form {
                display: flex;
                flex-direction: column;
                gap: 12px;
                padding: 16px;
                background: #f8f9fa;
                border: 1px solid #dee2e6;
                border-radius: 6px;
            }
            .form-group {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }
            .form-group label:not(.dataset-checkbox) {
                font-weight: 500;
                font-size: 13px;
                color: #6c757d;
            }
            .form-control {
                width: 100%;
                height: 34px;
                padding: 6px 10px;
                font-size: 14px;
                color: #495057;
                background-color: #fff;
                border: 1px solid #ced4da;
                border-radius: 4px;
                box-sizing: border-box;
                outline: none;
                transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
            }
            .form-control:focus {
                border-color: #007bff;
                box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
            }
            select.form-control { cursor: pointer; }

            /* Dataset filter styles */
            .dataset-filter-box {
                max-height: 200px;
                overflow-y: auto;
                border: 1px solid #ced4da;
                border-radius: 4px;
                background: #fff;
                padding: 6px 8px;
            }
            .dataset-checkbox {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 3px 0;
                cursor: pointer;
                font-size: 12px;
                font-weight: 400;
                color: #495057;
            }
            .dataset-checkbox input[type="checkbox"] {
                margin: 0;
                cursor: pointer;
                flex-shrink: 0;
            }
            .dataset-label-text {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .select-all-label {
                font-weight: 600;
            }
            .select-all-text {
                font-weight: 600;
                font-size: 12px;
            }
            .dataset-divider {
                border-top: 1px solid #dee2e6;
                margin: 4px 0;
            }
        `;
    }
}

customElements.define("lane-analytics-form", LaneAnalyticsForm);


/**
 * <lane-analytics-panel> Web Component
 * 
 * Combined component: filter form on the LEFT, chart on the RIGHT.
 * Form changes apply immediately. Includes multi-select dataset filter.
 * 
 * Layout:
 *   ┌──────────────┬──────────────────────────┐
 *   │  FORM (auto) │       CHART (flex: 1)    │
 *   └──────────────┴──────────────────────────┘
 */
class LaneAnalyticsPanel extends HTMLElement {
    static get observedAttributes() {
        return ["render"];
    }

    constructor() {
        super();
        this._shadowRoot = this.attachShadow({ mode: "open" });
        this._chart = null;
        this._form = null;
        this._initialized = false;
        this._chartObserver = null;
    }

    connectedCallback() {
        this._buildLayout();
        this._initialized = true;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === "render" && this._chart && this._initialized) {
            this._chart.setAttribute("render", newValue);
        }
    }

    disconnectedCallback() {
        if (this._form) {
            this._form.removeEventListener("analytics-form-change", this._boundFormChange);
            this._form.removeEventListener("analytics-dataset-filter", this._boundDatasetFilter);
        }
        if (this._chart) {
            this._chart.removeEventListener("analytics-legend-click", this._boundLegendClick);
        }
        if (this._chartObserver) {
            this._chartObserver.disconnect();
        }
        if (this._interpolationTimer) {
            clearTimeout(this._interpolationTimer);
        }
        if (this._syncPollTimer) {
            clearInterval(this._syncPollTimer);
        }
    }

    _buildLayout() {
        const hasDownload = this.hasAttribute("download");
        const hasCsv = this.hasAttribute("download-csv");
        const showDownloadSection = hasDownload || hasCsv;

        this._shadowRoot.innerHTML = `
            <style>${this._getPanelStyles()}</style>
            <div class="panel-container">
                <div class="panel-form" id="form-slot">
                </div>
                <div class="panel-chart" id="chart-slot"></div>
            </div>
        `;

        // Defer build until AngularJS has interpolated attributes (no more {{ }})
        if (this._hasUninterpolatedAttrs()) {
            this._interpolationTimer = setTimeout(() => this._buildContent(), 0);
            return;
        }
        this._buildContent();
    }

    /** Check if any attribute still contains AngularJS interpolation markers */
    _hasUninterpolatedAttrs() {
        for (const attr of this.attributes) {
            if (attr.value.includes("{{")) return true;
        }
        return false;
    }

    _buildContent() {
        const hasDownload = this.hasAttribute("download") && this.getAttribute("download") !== "false";
        const hasCsv = this.hasAttribute("download-csv") && this.getAttribute("download-csv") !== "false";
        const showDownloadSection = hasDownload || hasCsv;

        // Apply min-width to the panel host if specified
        const minWidth = this.getAttribute("min-width");
        if (minWidth) {
            this.style.minWidth = minWidth;
        }

        // Apply min-height to the chart slot so the panel container grows
        const minHeight = this.getAttribute("min-height");
        if (minHeight) {
            const chartSlotEl = this._shadowRoot.querySelector("#chart-slot");
            if (chartSlotEl) chartSlotEl.style.minHeight = minHeight;
        }

        // Create embedded <lane-analytics>
        this._chart = document.createElement("lane-analytics");
        this._forwardChartAttributes();
        this._chart.setAttribute("chart", "");
        if (!this._chart.hasAttribute("mode")) {
            this._chart.setAttribute("mode", "trend");
        }
        this._chart.setAttribute("render", "true");

        // Create embedded <lane-analytics-form>
        this._form = document.createElement("lane-analytics-form");
        this._forwardFormAttributes();

        // Track current separate-by value so we can detect dimension changes
        this._lastSeparateBy = this.getAttribute("datasets") || "";

        const chartSlot = this._shadowRoot.querySelector("#chart-slot");
        const formSlot = this._shadowRoot.querySelector("#form-slot");
        formSlot.appendChild(this._form);

        // Add download section below the form
        if (showDownloadSection) {
            const downloadSection = document.createElement("div");
            downloadSection.className = "panel-download";
            downloadSection.innerHTML = `<label class="download-label">Download</label><div class="download-buttons"></div>`;
            const btnContainer = downloadSection.querySelector(".download-buttons");

            if (hasDownload) {
                const pngBtn = document.createElement("button");
                pngBtn.className = "btn-download";
                pngBtn.style.cssText = "background-color: #337ab7; color: white; border: 1px solid #2e6da4;";
                pngBtn.innerHTML = `<div class="btn-download-inner"><i class="icon-image"></i><span>PNG</span></div>`;
                pngBtn.addEventListener("mouseover", function() { this.style.backgroundColor = "#0056b3"; });
                pngBtn.addEventListener("mouseout", function() { this.style.backgroundColor = "#337ab7"; });
                pngBtn.addEventListener("click", () => this._downloadPng());
                btnContainer.appendChild(pngBtn);
            }
            if (hasCsv) {
                const csvBtn = document.createElement("button");
                csvBtn.className = "btn-download";
                csvBtn.style.cssText = "background-color: #337ab7; color: white; border: 1px solid #2e6da4;";
                csvBtn.innerHTML = `<div class="btn-download-inner"><i class="icon-download"></i><span>CSV</span></div>`;
                csvBtn.addEventListener("mouseover", function() { this.style.backgroundColor = "#0056b3"; });
                csvBtn.addEventListener("mouseout", function() { this.style.backgroundColor = "#337ab7"; });
                csvBtn.addEventListener("click", () => this._downloadCsv());
                btnContainer.appendChild(csvBtn);
            }
            formSlot.appendChild(downloadSection);
        }

        chartSlot.appendChild(this._chart);

        // Listen for form changes and apply to chart
        this._boundFormChange = (e) => {
            // If the datasets dimension changed (separate-by), the new chart will have
            // completely different dataset labels — clear the hidden-label set so the
            // filter starts fresh instead of trying to match stale label names.
            const prevSeparateBy = this._lastSeparateBy;
            this._lastSeparateBy = e.detail.separateBy;
            if (e.detail.separateBy !== prevSeparateBy) {
                this._form._hiddenByLabel = null;
            }
            this._form._applyValuesToElement(this._chart, e.detail);
        };
        this._form.addEventListener("analytics-form-change", this._boundFormChange);

        // Listen for dataset filter changes (checkbox → chart)
        this._boundDatasetFilter = (e) => {
            this._applyDatasetVisibility(e.detail.hiddenIndices);
        };
        this._form.addEventListener("analytics-dataset-filter", this._boundDatasetFilter);

        // Listen for legend clicks on the chart (chart legend → checkbox)
        // When the user clicks a legend item in Chart.js, sync the checkbox state back.
        this._boundLegendClick = (e) => {
            this._syncCheckboxesFromChart(e.detail.hiddenIndices);
        };
        this._chart.addEventListener("analytics-legend-click", this._boundLegendClick);

        // Observe chart's render attribute to know when it re-renders,
        // then update dataset filter with new labels
        this._chartObserver = new MutationObserver(() => {
            this._waitForChartAndSync();
        });
        this._chartObserver.observe(this._chart, { attributes: true, attributeFilter: ["render"] });

        // Initial sync after first render
        this._waitForChartAndSync();
    }

    /**
     * Waits for the chart's Chart.js instance to be ready with data,
     * then syncs the dataset labels to the form filter.
     * Polls every 200ms for up to 3 seconds.
     */
    _waitForChartAndSync() {
        let attempts = 0;
        const maxAttempts = 15;
        // Capture the current chart instance — we wait until it changes (re-render complete)
        const chartBefore = this._chart?.chart;

        if (this._syncPollTimer) clearInterval(this._syncPollTimer);

        this._syncPollTimer = setInterval(() => {
            attempts++;
            const chart = this._chart?.chart;
            const datasets = chart?.data?.datasets || [];

            // Wait until: chart instance is new (re-rendered) with data, OR max attempts
            const isNewChart = chart && chart !== chartBefore;
            const hasData = datasets.length > 0;

            if ((isNewChart && hasData) || (attempts >= maxAttempts && hasData)) {
                clearInterval(this._syncPollTimer);
                this._syncPollTimer = null;
                const labels = datasets.map(ds => ds.label || "");
                this._form.updateDatasetFilter(labels);
            } else if (attempts >= maxAttempts) {
                clearInterval(this._syncPollTimer);
                this._syncPollTimer = null;
            }
        }, 200);
    }

    /** Show/hide datasets on the Chart.js instance based on filter */
    _applyDatasetVisibility(hiddenIndices) {
        if (!this._chart || !this._chart.chart) return;
        const chart = this._chart.chart;
        chart.data.datasets.forEach((ds, i) => {
            const meta = chart.getDatasetMeta(i);
            meta.hidden = hiddenIndices.includes(i);
        });
        chart.update();
    }

    /**
     * Syncs the form checkboxes to match the chart's current dataset visibility.
     * Called when the user clicks a legend item directly in the Chart.js UI.
     * @param {number[]} hiddenIndices - Dataset indices that are currently hidden
     */
    _syncCheckboxesFromChart(hiddenIndices) {
        if (!this._form) return;
        const container = this._form._shadowRoot &&
            this._form._shadowRoot.querySelector("#dataset-filter-list");
        if (!container) return;

        // Update individual dataset checkboxes
        container.querySelectorAll("input[type='checkbox'][data-index]").forEach(cb => {
            const idx = parseInt(cb.dataset.index, 10);
            cb.checked = !hiddenIndices.includes(idx);
        });

        // Update the "select all" master checkbox state
        const allCbs = [...container.querySelectorAll("input[data-index]")];
        const allChecked = allCbs.every(c => c.checked);
        const someChecked = allCbs.some(c => c.checked);
        const selectAll = container.querySelector("#dataset-select-all");
        if (selectAll) {
            selectAll.checked = allChecked;
            selectAll.indeterminate = !allChecked && someChecked;
        }

        // Keep the form's internal hidden-label set in sync so it survives re-renders
        if (this._form._datasetLabels && this._form._datasetLabels.length > 0) {
            const newHidden = new Set();
            hiddenIndices.forEach(i => {
                if (this._form._datasetLabels[i]) {
                    newHidden.add(this._form._datasetLabels[i]);
                }
            });
            this._form._hiddenByLabel = newHidden;
        }
    }

    /** Download chart as PNG image */
    _downloadPng() {
        if (!this._chart || !this._chart.chart) return;
        const a = document.createElement("a");
        a.href = this._chart.chart.toBase64Image();
        a.download = (this.getAttribute("graph-title") || "chart") + ".png";
        a.click();
        a.remove();
    }

    /** Download chart data as CSV */
    _downloadCsv() {
        if (!this._chart) return;
        // Trigger the chart's built-in CSV download function
        if (typeof this._chart.downloadCsvFunction === "function") {
            this._chart.downloadCsvFunction.call(this._chart);
        }
    }

    _forwardChartAttributes() {
        const chartAttrs = [
            "language", "metrics", "span", "datasets", "chart-type", "color",
            "mode", "type", "span-dropdown", "min-width", "min-height",
            "graph-title", "graph-subtitle", "title-align", "filters"
        ];
        const dateAttrs = ["begin-date", "end-date", "today", "last24h", "lastWeek", "last30gg"];
        const allAttrs = [...chartAttrs, ...dateAttrs];

        for (const attr of allAttrs) {
            if (this.hasAttribute(attr)) {
                const val = this.getAttribute(attr);
                // Skip boolean attributes that are explicitly "false"
                if (val === "false") continue;
                this._chart.setAttribute(attr, val);
            }
        }

        // Download attributes are NOT forwarded to the chart — the panel
        // handles them in its own dedicated download section.

        if (!this.hasAttribute("begin-date") && !this.hasAttribute("today") &&
            !this.hasAttribute("last24h") && !this.hasAttribute("lastWeek") && !this.hasAttribute("last30gg")) {
            const days = parseInt(this.getAttribute("days") || "7", 10);
            if (!isNaN(days)) {
                const begin = new Date();
                begin.setDate(begin.getDate() - days);
                begin.setHours(0, 0, 0, 0);
                this._chart.setAttribute("begin-date", begin.toISOString());
                const end = new Date();
                end.setHours(23, 59, 0, 0);
                this._chart.setAttribute("end-date", end.toISOString());
            }
        }
    }

    _forwardFormAttributes() {
        const formAttrs = [
            "language", "days", "begin-date", "end-date",
            "show-separate-by", "show-type", "show-mode", "show-span",
            "datasets", "type", "span", "mode"
        ];
        for (const attr of formAttrs) {
            if (this.hasAttribute(attr)) {
                this._form.setAttribute(attr, this.getAttribute(attr));
            }
        }
    }

    _getPanelStyles() {
        return `
            :host {
                display: block;
                width: 100%;
            }
            @font-face {
                font-family: 'icon-font';
                src: url('/analytics/1.0/res/css/fonts/font.eot?gxcbg6');
                src: url('/analytics/1.0/res/css/fonts/font.eot?gxcbg6#iefix') format('embedded-opentype'),
                     url('/analytics/1.0/res/css/fonts/font.ttf?gxcbg6') format('truetype'),
                     url('/analytics/1.0/res/css/fonts/font.woff?gxcbg6') format('woff'),
                     url('/analytics/1.0/res/css/fonts/font.svg?gxcbg6#font') format('svg');
                font-weight: normal;
                font-style: normal;
                font-display: block;
            }
            .panel-container {
                display: flex;
                flex-direction: row;
                gap: 16px;
                width: 100%;
                min-height: 28rem;
            }
            .panel-form {
                flex: 0 0 auto;
                width: 240px;
                min-width: 200px;
            }
            .panel-chart {
                flex: 1;
                min-width: 0;
                position: relative;
            }
            .panel-download {
                margin-top: 12px;
                padding: 12px 16px;
                background: #f8f9fa;
                border: 1px solid #dee2e6;
                border-radius: 6px;
            }
            .download-label {
                display: block;
                font-weight: 500;
                font-size: 13px;
                color: #6c757d;
                margin-bottom: 8px;
                font-family: 'Arial', sans-serif;
            }
            .download-buttons {
                display: flex;
                gap: 8px;
            }
            .btn-download {
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
                border-radius: 6px;
                padding: 8px 15px;
                cursor: pointer;
                font-weight: 400;
                font-size: 14px;
                font-family: 'Arial', sans-serif;
            }
            .btn-download-inner {
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 10px;
            }
            .btn-download-inner i {
                font-family: 'icon-font', sans-serif !important;
                font-size: 18px;
                font-style: normal;
                font-weight: normal;
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
            }
            .btn-download-inner i.icon-image::before { content: "\\e902"; }
            .btn-download-inner i.icon-download::before { content: "\\e901"; }
            @media (max-width: 768px) {
                .panel-container {
                    flex-direction: column;
                }
                .panel-form {
                    width: 100%;
                }
            }
        `;
    }
}

customElements.define("lane-analytics-panel", LaneAnalyticsPanel);
